import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  IconButton,
  Button,
  Box,
} from '@material-ui/core';
import { Delete as DeleteIcon, Add as AddIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

const JsonTableEditor = ({
  data,
  onChange,
  columns,
  addButtonLabel = 'Ajouter une ligne',
  emptyMessage = 'Aucune donnée',
}) => {
  const classes = useStyles();

  const handleCellChange = (rowIndex, columnKey, value) => {
    const newData = [...data];
    newData[rowIndex] = {
      ...newData[rowIndex],
      [columnKey]: value,
    };
    onChange(newData);
  };

  const handleAddRow = () => {
    const newRow = {};
    columns.forEach(col => {
      newRow[col.key] = col.type === 'boolean' ? 'false' : '';
      if (col.default) {
        newRow[col.key] = col.default;
      }
    });
    onChange([...data, newRow]);
  };

  const handleDeleteRow = rowIndex => {
    const newData = data.filter((_, index) => index !== rowIndex);
    onChange(newData);
  };

  const renderCell = (row, column, rowIndex) => {
    const value = row[column.key];

    switch (column.type) {
      case 'boolean':
        return (
          <Checkbox
            checked={value === 'true' || value === true}
            onChange={e =>
              handleCellChange(rowIndex, column.key, e.target.checked ? 'true' : 'false')
            }
            color="primary"
          />
        );
      case 'text':
      default:
        return (
          <TextField
            value={value || ''}
            onChange={e => handleCellChange(rowIndex, column.key, e.target.value)}
            placeholder={column.placeholder || ''}
            variant="standard"
            size="small"
            fullWidth
          />
        );
    }
  };

  return (
    <Box className={classes.container}>
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.key} style={col.style || {}}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell align="center" style={{ width: '1%' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map(col => (
                    <TableCell key={col.key}>{renderCell(row, col, rowIndex)}</TableCell>
                  ))}
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleDeleteRow(rowIndex)}
                      className={classes.deleteButton}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddRow}
        className={classes.addButton}
      >
        {addButtonLabel}
      </Button>
    </Box>
  );
};

JsonTableEditor.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'boolean']).isRequired,
      placeholder: PropTypes.string,
      default: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      style: PropTypes.object,
    }),
  ).isRequired,
  addButtonLabel: PropTypes.string,
  emptyMessage: PropTypes.string,
};

JsonTableEditor.defaultProps = {
  addButtonLabel: 'Ajouter une ligne',
  emptyMessage: 'Aucune donnée',
};

export default JsonTableEditor;
